import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Box, Divider, Typography, makeStyles } from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import DeviceStatus from "../DeviceList/DeviceStatus";
import Sensor from "../DeviceList/Sensor";
import FormattedDate from "../../elements/FormattedDate";
import I18nText from "../../elements/I18nText";
import { DEVICE_STATUS } from "../../domain/device";
import AlarmMessages from "./AlarmMessages";

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 240
  },
  titleLink: {
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
    "&:hover": {
      color: theme.palette.primary.main,
      textDecoration: "underline"
    }
  },
  titleIcon: {
    fontSize: 16,
    marginLeft: theme.spacing(0.25)
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(0.25, 0)
  },
  label: {
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap"
  },
  value: {
    textAlign: "right",
    wordBreak: "break-word"
  },
  sensorsTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary
  },
  sensorRow: {
    display: "flex",
    paddingTop: theme.spacing(0.5)
  },
  sensorSequence: {
    minWidth: 26,
    flexGrow: 0
  },
  sensorValue: {
    flexGrow: 1
  },
  footer: {
    marginTop: theme.spacing(1),
    display: "block"
  }
}));

const formatAddress = address => {
  if (!address) {
    return null;
  }

  const line1 = [address.street, address.streetNumber]
    .filter(Boolean)
    .join(" ");
  const line2 = [address.postalCode, address.city].filter(Boolean).join(" ");

  return [line1, line2].filter(Boolean).join(", ");
};

const InfoRow = ({ labelKey, value }) => {
  const classes = useStyles();

  if (!value) {
    return null;
  }

  return (
    <Box className={classes.infoRow}>
      <Typography variant="caption" className={classes.label}>
        <I18nText>{labelKey}</I18nText>
      </Typography>
      <Typography variant="caption" className={classes.value}>
        {value}
      </Typography>
    </Box>
  );
};

const MapMarkerPopup = ({ device, sensorSettings }) => {
  const classes = useStyles();

  const connectedSensors = useMemo(
    () => device.sensors.filter(sensor => sensor.isConnected),
    [device.sensors]
  );

  const address = formatAddress(device.address);
  const navLink = `https://maps.google.de/maps/place/${device.location.lat},${device.location.lng}`;

  const activeAlarms = useMemo(() => {
    if (device.status !== DEVICE_STATUS.ALARM || !device.alarms) {
      return [];
    }

    const unconfirmed = device.alarms.filter(
      alarm => !alarm.confirmationStatus
    );

    return unconfirmed.length > 0 ? unconfirmed : device.alarms;
  }, [device.status, device.alarms]);

  return (
    <Box className={classes.root}>
      <Typography
        component={Link}
        to={`/devices/${device.id}`}
        className={classes.titleLink}
      >
        #{device.id} {device.name}
        <ChevronRightIcon className={classes.titleIcon} />
      </Typography>

      <Box marginBottom={0.5}>
        <DeviceStatus device={device} />
      </Box>

      {activeAlarms.length > 0 && <AlarmMessages alarms={activeAlarms} />}

      <Divider />

      <Box marginTop={0.5}>
        <InfoRow
          labelKey="devices.list.map.popup.project"
          value={device.project}
        />
        <InfoRow labelKey="devices.list.map.popup.address" value={address} />
        <InfoRow
          labelKey="devices.list.map.popup.positionAsOf"
          value={<FormattedDate>{device.location.updatedAt}</FormattedDate>}
        />
        <InfoRow
          labelKey="devices.list.map.popup.lastReported"
          value={<FormattedDate>{device.lastReportedAt}</FormattedDate>}
        />
      </Box>

      {connectedSensors.length > 0 && (
        <>
          <Typography variant="caption" className={classes.sensorsTitle}>
            <I18nText>devices.list.map.popup.sensorsTitle</I18nText>
          </Typography>
          <Box>
            {connectedSensors.map(sensor => (
              <Box className={classes.sensorRow} key={sensor.id}>
                <Box className={classes.sensorSequence}>{sensor.sequence}:</Box>
                <Box className={classes.sensorValue}>
                  <Sensor sensor={sensor} sensorSettings={sensorSettings} />
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      <a
        className={classes.footer}
        href={navLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        <OpenInNewIcon style={{ fontSize: 12, verticalAlign: "middle" }} />{" "}
        <I18nText>devices.list.map.popup.openInGoogleMaps</I18nText>
      </a>
    </Box>
  );
};

export default React.memo(MapMarkerPopup);
